import { useState } from 'react';

import { router } from '@inertiajs/react';
import { FolderCog } from 'lucide-react';

import {
    WishlistCollectionOption,
    WishlistCollectionsDialog,
} from './WishlistCollectionsDialog';
import { wishlistItemCollectionsRoute } from '../../models/routes';
import { csrfTokenHeader } from '../../services/CsrfTokenService';
import { cn } from '../../utils';
import { Button } from '../ui/Button';

type Props = {
    productId: number;
    className?: string;
};

const responseError = async (response: Response) => {
    const payload = await response.json().catch(() => null);
    return payload?.errors
        ? Object.values(payload.errors).join(' ')
        : 'Не вдалося оновити колекції';
};

export const WishlistCollectionManagerButton = ({
    productId,
    className,
}: Props) => {
    const [open, setOpen] = useState(false);
    const [collections, setCollections] = useState<WishlistCollectionOption[]>(
        [],
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadCollections = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(
                wishlistItemCollectionsRoute(productId),
                { headers: { Accept: 'application/json' } },
            );
            if (!response.ok) throw new Error(await responseError(response));

            const payload = await response.json();
            setCollections(payload.collections);
        } catch (requestError) {
            setError((requestError as Error).message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (nextOpen) void loadCollections();
    };

    const saveCollections = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(
                wishlistItemCollectionsRoute(productId),
                {
                    method: 'PATCH',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        ...csrfTokenHeader(),
                    },
                    body: JSON.stringify({
                        collection_ids: collections
                            .filter((collection) => collection.selected)
                            .map((collection) => collection.id),
                    }),
                },
            );
            if (!response.ok) throw new Error(await responseError(response));

            setOpen(false);
            router.reload({
                only: ['products', 'collections', 'nextPage'],
            });
        } catch (requestError) {
            setError((requestError as Error).message);
        } finally {
            setLoading(false);
        }
    };

    const updateCollection = (collectionId: number, selected: boolean) => {
        setCollections((current) =>
            current.map((collection) =>
                collection.id === collectionId
                    ? { ...collection, selected }
                    : collection,
            ),
        );
    };

    const selectAll = () => {
        setCollections((current) =>
            current.map((collection) => ({ ...collection, selected: false })),
        );
    };

    return (
        <>
            <Button
                type="button"
                variant="outline"
                size="icon"
                className={cn(
                    'bg-background/90 shadow-sm backdrop-blur-sm',
                    className,
                )}
                onClick={() => handleOpenChange(true)}
                aria-label="Керувати колекціями товару"
                title="Керувати колекціями товару"
                data-testid={`wishlist-collections-button-${productId}`}
            >
                <FolderCog className="h-4 w-4" />
            </Button>

            <WishlistCollectionsDialog
                open={open}
                onOpenChange={handleOpenChange}
                title="Колекції товару"
                description="Товар завжди залишається в загальному списку. Щоб перенести його, зніміть попередню колекцію та оберіть нову."
                collections={collections}
                loading={loading}
                error={error}
                submitLabel="Зберегти"
                emptyMessage="Інших колекцій ще немає. Створити колекцію можна на сторінці списку бажаного."
                onSelectAll={selectAll}
                onCollectionChange={updateCollection}
                onSubmit={saveCollections}
            />
        </>
    );
};
